import { IRotaManagementShiftListData, IRotaManagementShiftTimings } from "@/api_framework/api_modals/Rota";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";
import CheckBox from "@/app/components/ui/CheckBoxes/FormCheckbox/index";
import Input from "@/app/components/ui/Inputs/Input/index";
import NonSearchableSelectMenu from "@/app/components/ui/SelectMenus/NonSearchableSelectMenu/index";
import { ISelectMenuItemData } from "@/app/components/ui/interface";
import { useRotaShiftCreateAPI } from "@/app/hooks/api_hooks/Rota/useRotaShiftCreateAPI";
import { useRotaShiftDetailsAPI } from "@/app/hooks/api_hooks/Rota/useRotaShiftDetailsAPI";
import { useRotaShiftUpdateAPI } from "@/app/hooks/api_hooks/Rota/useRotaShiftUpdateAPI";
import { useArrayConstants } from "@/app/hooks/useArrayConstants";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import { IRotaState, setShiftDetails } from "@/app_redux/reducers/slices/auth/rota_slice";
import { MinusIcon, PlusIcon } from "@heroicons/react/24/outline";
import { useEffect } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";

export interface IRotaShiftCreateFormFields {
  shiftTimings: IRotaManagementShiftTimings[];
  shift: string;
  startTimeHour: string;
  startTimeMinute: string;
  endTimeHour: string;
  endTimeMinute: string;
  is_pet_active: boolean;
  is_pet_inactive: boolean;
}

const AddNewShiftComponent = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { getRouteKey } = useRouter();
  const [params, _setparams] = useSearchParams();
  const routeId = getRouteKey("ROTA", "id");
  const { routeState } = useRouteInfo(routeId);
  const { shiftDetails } = routeState.state as IRotaState;
  const arrayConstants = useArrayConstants();
  const shiftUpdateAPI = useRotaShiftUpdateAPI();
  const { execute: standardShiftCreate } = useRotaShiftCreateAPI();
  const fetchShiftDetailsAPI = useRotaShiftDetailsAPI();
  const startTimeItem = arrayConstants.overBookingStartTimeItems as ISelectMenuItemData[];
  const startTimeMinuteItem = arrayConstants.overBookingStartTimeMinuteItems as ISelectMenuItemData[];

  const formHook = useForm<IRotaShiftCreateFormFields>({
    mode: "onChange",
    defaultValues: {
      shiftTimings: [
        {
          end_time: "",
          end_time_hour: "",
          end_time_minute: "",
          start_time: "",
          start_time_hour: "",
          start_time_minute: "",
        },
      ],
    },
  });

  const shiftTimingsFormHook = useFieldArray<IRotaShiftCreateFormFields>({
    name: "shiftTimings",
    control: formHook.control,
  });

  useEffect(() => {
    const shiftId = params.get("shift_id");
    if (shiftId) {
      fetchShiftDetailsAPI(shiftId);
    } else {
      formHook.setValue("is_pet_active", true);
    }
  }, []);

  useEffect(() => {
    if (shiftDetails) {
      formHook.setValue("shift", shiftDetails?.shift_name);
      formHook.setValue(
        "shiftTimings",
        shiftDetails?.shift_timings?.map((st) => {
          return {
            ...st,
            start_time_hour: st.start_time.split(":")[0],
            start_time_minute: st.start_time.split(":")[1],
            end_time_hour: st.end_time.split(":")[0],
            end_time_minute: st.end_time.split(":")[1],
          };
        }),
      );
      if (shiftDetails.shift_status) {
        formHook.setValue("is_pet_active", true);
      } else {
        formHook.setValue("is_pet_inactive", true);
      }
    }
  }, [shiftDetails]);

  const handleAddTime = () => {
    shiftTimingsFormHook.append({
      end_time: "",
      start_time: "",
      end_time_hour: "",
      end_time_minute: "",
      start_time_hour: formHook.getValues(
        `shiftTimings.${shiftTimingsFormHook.fields.length - 1}.end_time_hour` as const,
      ),
      start_time_minute: formHook.getValues(
        `shiftTimings.${shiftTimingsFormHook.fields.length - 1}.end_time_minute` as const,
      ),
    });
  };

  const handleRemoveShiftTimin = (index: number) => {
    shiftTimingsFormHook.remove(index);
  };

  useEffect(() => {
    const form_data = formHook.getValues();
    if (form_data.is_pet_active) {
      formHook.setValue("is_pet_active", true);
      formHook.setValue("is_pet_inactive", false);
    } else {
      formHook.setValue("is_pet_inactive", true);
      formHook.setValue("is_pet_active", false);
    }
  }, [formHook.watch("is_pet_active"), formHook.watch("is_pet_inactive")]);

  const constructStandardShiftPayload = (): IRotaManagementShiftListData => {
    const form_data = formHook.getValues();
    let constructedBody: IRotaManagementShiftListData = {
      shift_name: form_data?.shift,
      shift_status: form_data.is_pet_active === true ? true : form_data.is_pet_inactive === true && false,
      shift_timings: null,
    };
    if (shiftDetails) {
      constructedBody = {
        ...constructedBody,
        id: shiftDetails?.id,
      };
    }
    constructedBody = {
      ...constructedBody,
      shift_timings: form_data?.shiftTimings?.map((st) => {
        return {
          ...st,
          start_time: `${st?.start_time_hour}:${st?.start_time_minute}`,
          end_time: `${st?.end_time_hour}:${st?.end_time_minute}`,
        };
      }),
    };
    return constructedBody;
  };

  const handelClick = () => {
    const contructedPayload = constructStandardShiftPayload();
    if (shiftDetails) {
      shiftUpdateAPI(contructedPayload, shiftDetails?.id).then(async (response) => {
        if (response.status) {
          await navigate(`/app/rota/shift-details`);
          await dispatch(setShiftDetails(null));
        } else {
          toast.error(response.message);
        }
      });
    } else {
      standardShiftCreate(contructedPayload).then(async (response) => {
        if (response.status) {
          await navigate(`/app/rota/shift-details`);
        } else {
          toast.error(response.message);
        }
      });
    }
  };

  const calculateStartTimeHourList = (index: number) => {
    return startTimeItem;
  };

  const calculateEndTimeHourList = (index: number) => {
    return startTimeItem;
  };

  const handelShiftDetails = () => {
    navigate(`/app/rota/shift-details`);
  };

  return (
    <div className="bg-white px-4 py-3 rounded-md">
      <h2 className="font-medium">Add Staff Duty Schedule</h2>
      <div className="flex flex-col gap-8 mt-8">
        <div className="flex items-center">
          <div className="w-2/12">
            <p>
              Name <span className="text-red-400">*</span>
            </p>
          </div>
          <div className="w-3/6">
            <Input
              className="text-xs"
              placeholder="Shift name"
              register={formHook.register("shift", {
                required: true,
              })}
              fieldError={formHook.formState.errors.shift}
              errorMessages={[]}
            />
          </div>
        </div>
        <div className="flex items-center">
          <div className="w-2/12">
            <p>
              Set up Time <span className="text-red-400">*</span>
            </p>
          </div>
          <div className="flex flex-col w-3/6">
            <div className="grid grid-cols-2">
              <p className="font-medium">Start Time</p>
              <p className="font-medium">End Time</p>
            </div>

            {shiftTimingsFormHook?.fields?.map((field, index) => {
              return (
                <div key={field.id} className="relative flex items-center gap-2">
                  <div className="gap-5 grid grid-cols-2 w-full">
                    <div className="items-center gap-4 grid grid-cols-2 mt-2">
                      <NonSearchableSelectMenu
                        errorMessages={[{ message: "Required", type: "required" }]}
                        register={formHook.register(`shiftTimings.${index}.start_time_hour`, { required: true })}
                        fieldError={
                          formHook?.formState?.errors?.shiftTimings
                            ? formHook?.formState?.errors?.shiftTimings?.[index]
                              ? formHook?.formState?.errors?.shiftTimings?.[index]?.start_time_hour
                              : null
                            : null
                        }
                        onSelectItem={(item) => {
                          if (item) {
                            formHook.setValue(`shiftTimings.${index}.start_time_hour` as const, item.title);
                          }
                        }}
                        selectItems={calculateStartTimeHourList(index)}
                        placeholder="HH"
                        showTooltips={false}
                        showTypedErrors={false}
                        showDropdownIcon={true}
                        defaultSelected={
                          calculateStartTimeHourList(index)?.filter(
                            (oc) => oc.title === formHook.watch(`shiftTimings.${index}.start_time_hour`),
                          )[0]
                        }
                        listBoxClassName="w-full"
                        className="text-gray-800"
                        containerClassName="w-full"
                      />
                      <NonSearchableSelectMenu
                        errorMessages={[{ message: "Required", type: "required" }]}
                        register={formHook.register("startTimeMinute", { required: true })}
                        fieldError={
                          formHook?.formState?.errors?.shiftTimings
                            ? formHook?.formState?.errors?.shiftTimings?.[index]
                              ? formHook?.formState?.errors?.shiftTimings?.[index]?.start_time_minute
                              : null
                            : null
                        }
                        onSelectItem={(item) => {
                          if (item) {
                            formHook.setValue(`shiftTimings.${index}.start_time_minute` as const, item.title);
                          }
                        }}
                        selectItems={startTimeMinuteItem}
                        placeholder="MM"
                        showTooltips={false}
                        showTypedErrors={false}
                        showDropdownIcon={true}
                        defaultSelected={
                          startTimeMinuteItem?.filter(
                            (oc) => oc.title === formHook.watch(`shiftTimings.${index}.start_time_minute`),
                          )[0]
                        }
                        listBoxClassName="w-full"
                        className="text-gray-800"
                        containerClassName="w-full"
                      />
                    </div>

                    <div className="items-center gap-4 grid grid-cols-2 mt-2">
                      <NonSearchableSelectMenu
                        errorMessages={[{ message: "required", type: "required" }]}
                        register={formHook.register(`shiftTimings.${index}.end_time_hour`, { required: true })}
                        fieldError={
                          formHook?.formState?.errors?.shiftTimings
                            ? formHook?.formState?.errors?.shiftTimings?.[index]
                              ? formHook?.formState?.errors?.shiftTimings?.[index]?.end_time_hour
                              : null
                            : null
                        }
                        onSelectItem={(item) => {
                          if (item) {
                            formHook.setValue(`shiftTimings.${index}.end_time_hour`, item.title);
                          }
                        }}
                        selectItems={calculateEndTimeHourList(index)}
                        placeholder="HH"
                        showTooltips={false}
                        showTypedErrors={false}
                        showDropdownIcon={true}
                        defaultSelected={
                          calculateEndTimeHourList(index)?.filter(
                            (oc) => oc.title === formHook.watch(`shiftTimings.${index}.end_time_hour`),
                          )[0]
                        }
                        listBoxClassName="w-full"
                        className="text-gray-800"
                        containerClassName="w-full"
                      />

                      <NonSearchableSelectMenu
                        errorMessages={[{ message: "shift is required", type: "required" }]}
                        onSelectItem={(item) => {
                          if (item) {
                            formHook.setValue(`shiftTimings.${index}.end_time_minute`, item.title);
                          }
                        }}
                        fieldError={
                          formHook?.formState?.errors?.shiftTimings
                            ? formHook?.formState?.errors?.shiftTimings?.[index]
                              ? formHook?.formState?.errors?.shiftTimings?.[index]?.end_time_minute
                              : null
                            : null
                        }
                        register={formHook.register(`shiftTimings.${index}.end_time_minute`, { required: true })}
                        selectItems={startTimeMinuteItem}
                        placeholder="MM"
                        showTooltips={false}
                        showTypedErrors={false}
                        showDropdownIcon={true}
                        defaultSelected={
                          startTimeMinuteItem?.filter(
                            (oc) => oc.title === formHook.watch(`shiftTimings.${index}.end_time_minute`),
                          )[0]
                        }
                        listBoxClassName="w-full"
                        className="text-gray-800"
                        containerClassName="w-full"
                      />
                    </div>
                    {index === shiftTimingsFormHook.fields.length - 1 && (
                      <div className="top-3 -right-20 absolute">
                        {index !== 0 && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleRemoveShiftTimin(index);
                            }}
                            className="border-gray-200 bg-white hover:bg-blue-200 shadow-md p-1 border rounded-md hover:text-blue-800"
                          >
                            <MinusIcon className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleAddTime();
                          }}
                          className="border-gray-200 bg-white hover:bg-blue-200 shadow-md ml-2 p-1 border rounded-md hover:text-blue-800"
                        >
                          <PlusIcon className="w-5 h-5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex items-center">
          <div className="w-2/12">
            <p>
              Status <span className="text-red-400"></span>
            </p>
          </div>
          <div className="flex items-center gap-6 w-3/6">
            <div className="flex justify-start items-center">
              <CheckBox
                circular={true}
                colorHash="#22c55e"
                onCheckChange={() => {
                  formHook.setValue("is_pet_active", true);
                  formHook.setValue("is_pet_inactive", false);
                }}
                fieldError={formHook.formState.errors.is_pet_active}
                register={formHook.register("is_pet_active")}
                errorMessages={[]}
              />
              <span className="text-gray-500 text-left">Active</span>
            </div>
            <div className="flex justify-start items-center">
              <CheckBox
                circular={true}
                colorHash="#3b82f6"
                disabled={false}
                onCheckChange={() => {
                  formHook.setValue("is_pet_inactive", true);
                  formHook.setValue("is_pet_active", false);
                }}
                fieldError={formHook.formState.errors.is_pet_inactive}
                register={formHook.register("is_pet_inactive")}
                errorMessages={[]}
              />
              <span className="text-gray-500 text-left">InActive</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-4 p-4 items-center border-t-2 w-full fixed bg-white bottom-0 right-0">
        <button className={SECONDARY_BUTTON} onClick={() => handelShiftDetails()}>
          Cancel
        </button>
        <button className={PRIMARY_BUTTON} onClick={() => handelClick()}>
          {shiftDetails ? "Update" : "Save"}
        </button>
      </div>
    </div>
  );
};
export default AddNewShiftComponent;
